import { BlockStack, Box, Card, InlineStack, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'
import { CardHeader } from './LensBadge'

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000)     return `${Math.round(n / 1_000)}K`
  return Math.round(n).toLocaleString('en-US')
}

// Turn "2026-07" into "Jul 2026". Falls back to the raw label for the synthetic
// 'older' / 'unknown' cohorts.
function cohortLabel(cohortMonth: string, label: string): string {
  if (!/^\d{4}-\d{2}$/.test(cohortMonth)) return label
  const [y, m] = cohortMonth.split('-')
  const date = new Date(Number(y), Number(m) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

type Props = { data: BccData }

export function SettlementBridge({ data }: Props) {
  const { bridge } = data.cash
  const thisMonthPct = Math.round(bridge.thisMonthPct)

  // A month whose cash is mostly older work looks strong while current work may
  // be weak — that is the whole reason this card exists.
  const tone =
    thisMonthPct >= 50 ? 'success' :
    thisMonthPct >= 30 ? undefined : 'critical'

  return (
    <Card>
      <BlockStack gap="400">
        <CardHeader title="Where This Month's Cash Came From" lens="cash" />

        <InlineStack align="space-between" blockAlign="baseline">
          <Text as="p" variant="bodySm" tone="subdued">Collected this month</Text>
          <Text as="p" variant="headingLg">{`EGP ${egp(bridge.totalCollected)}`}</Text>
        </InlineStack>

        {bridge.totalCollected === 0 ? (
          <Text as="p" variant="bodySm" tone="subdued">
            No settlements recorded for this month yet.
          </Text>
        ) : (
          <>
            {/* Stacked share bar — this month's own work versus everything older */}
            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width:      `${thisMonthPct}%`,
                background: 'var(--p-color-bg-fill-success)',
              }} />
              <div style={{
                flex:       1,
                background: 'var(--p-color-bg-fill-tertiary)',
              }} />
            </div>

            <BlockStack gap="150">
              {bridge.cohorts.map(c => {
                const isThisMonth = c.cohortMonth === bridge.month
                return (
                  <Box
                    key={c.cohortMonth}
                    background={isThisMonth ? 'bg-surface-success' : 'bg-surface-secondary'}
                    padding="300"
                    borderRadius="200"
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" fontWeight={isThisMonth ? 'semibold' : undefined}>
                          {isThisMonth
                            ? 'From this period’s own orders'
                            : `From ${cohortLabel(c.cohortMonth, c.label)}`}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {`${c.orderCount} ${c.orderCount === 1 ? 'order' : 'orders'}`}
                        </Text>
                      </BlockStack>
                      <InlineStack gap="300" blockAlign="baseline">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          {`EGP ${egp(c.amount)}`}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {`${Math.round(c.pct)}%`}
                        </Text>
                      </InlineStack>
                    </InlineStack>
                  </Box>
                )
              })}
            </BlockStack>

            <Text as="p" variant="bodySm" tone={tone}>
              {thisMonthPct >= 50
                ? `${thisMonthPct}% of this month's cash came from this month's own orders.`
                : `Only ${thisMonthPct}% of this month's cash came from this month's own orders — the rest is earlier work settling.`}
            </Text>
          </>
        )}
      </BlockStack>
    </Card>
  )
}
